import { useState, useEffect, useCallback } from 'react';
import { auth, db } from '@/lib/firebase';
import { collection, query, where, getDocs, addDoc, deleteDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getListingById, getListingBySlug } from '@/data/listing-details.data';

/**
 * Favorites hook backed 100% by Firestore (favorites collection).
 * No localStorage dependency.
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const getUserId = () => auth.currentUser?.uid || null;
  const normalizeStorageSlug = (value: string) => getListingById(value)?.slug || getListingBySlug(value)?.slug || value;
  const resolveDealId = (value: string) => getListingBySlug(value)?.id || getListingById(value)?.id || null;

  useEffect(() => {
    let unsubscribeSnapshot: (() => void) | undefined;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      unsubscribeSnapshot?.();

      if (!user) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      const favRef = collection(db, 'favorites');
      const q = query(favRef, where('buyerId', '==', user.uid));

      unsubscribeSnapshot = onSnapshot(
        q,
        (snapshot) => {
          const slugs = Array.from(
            new Set(
              snapshot.docs
                .map((doc) => {
                  const data = doc.data();
                  const rawValue = data.listingSlug || data.dealId || null;
                  return rawValue ? normalizeStorageSlug(rawValue) : null;
                })
                .filter((value): value is string => Boolean(value)),
            ),
          );

          setFavorites(slugs);
          setLoading(false);
        },
        (error) => {
          console.error('Error loading favorites from Firestore:', error);
          setLoading(false);
        },
      );
    });

    return () => {
      unsubscribeSnapshot?.();
      unsubscribeAuth();
    };
  }, []);

  const isFavorite = useCallback((listingSlug: string) => {
    return favorites.includes(listingSlug);
  }, [favorites]);

  const toggleFavorite = useCallback(async (listingSlug: string): Promise<boolean> => {
    const uid = getUserId();
    if (!uid) return false;

    const storageSlug = normalizeStorageSlug(listingSlug);
    const listingId = resolveDealId(listingSlug);
    const isCurrentlyFavorite = favorites.includes(storageSlug);

    try {
      const favRef = collection(db, 'favorites');
      const buyerFavoritesQuery = query(favRef, where('buyerId', '==', uid));

      if (isCurrentlyFavorite) {
        const snapshot = await getDocs(buyerFavoritesQuery);
        const deletePromises = snapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            const savedSlug = data.listingSlug ? normalizeStorageSlug(data.listingSlug) : null;
            const savedDealId = data.dealId ? resolveDealId(data.dealId) || data.dealId : null;

            return savedSlug === storageSlug || savedDealId === listingId;
          })
          .map((docSnap) => deleteDoc(docSnap.ref));

        await Promise.all(deletePromises);

        setFavorites((prev) => prev.filter((slug) => slug !== storageSlug));
        return false;
      } else {
        await addDoc(favRef, {
          buyerId: uid,
          listingSlug: storageSlug,
          dealId: listingId,
          addedAt: serverTimestamp(),
        });

        setFavorites((prev) => (prev.includes(storageSlug) ? prev : [...prev, storageSlug]));
        return true;
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return isCurrentlyFavorite;
    }
  }, [favorites]);

  const addFavorite = useCallback(async (listingSlug: string) => {
    if (!favorites.includes(listingSlug)) {
      await toggleFavorite(listingSlug);
    }
  }, [favorites, toggleFavorite]);

  const removeFavorite = useCallback(async (listingSlug: string) => {
    if (favorites.includes(listingSlug)) {
      await toggleFavorite(listingSlug);
    }
  }, [favorites, toggleFavorite]);

  const clearFavorites = useCallback(async () => {
    const uid = getUserId();
    if (!uid) return;

    try {
      const favRef = collection(db, 'favorites');
      const q = query(favRef, where('buyerId', '==', uid));
      const snapshot = await getDocs(q);
      await Promise.all(snapshot.docs.map(docSnap => deleteDoc(docSnap.ref)));
      setFavorites([]);
    } catch (error) {
      console.error('Error clearing favorites:', error);
    }
  }, []);

  return {
    favorites,
    favoritesCount: favorites.length,
    isFavorite,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    clearFavorites,
    loading,
  };
}
